import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import api from "../lib/apiClient";
import { notifications } from "@mantine/notifications";
import { FORM_MUTATION_META } from "../utils/formErrorHandler";
import type { Database } from "../types/database";
import { useFacilityContext } from "./useFacilityContext";
import { mintReauthCredentialIfPossible } from "./useAuth";
import { reauthCredential } from "../lib/native/reauthCredential";
import { listReauthProfiles } from "../lib/native/reauthProfileCache";
import { isReauthClientConfigured } from "../lib/cognitoClients";

// Clock-in/out reliability: these are timekeeping actions (NOT PHI) punched on
// flaky facility WiFi. A transient transport failure shouldn't force a manual
// re-punch — retry it a couple of times with backoff before surfacing the error.
// 4xx responses are deterministic (e.g. "Already clocked out" from the
// row-locked stored proc) and must NOT be retried — only network drops
// (no response) and 5xx server blips. Cap at 2 retries to bound the worst case.
const retryClockAction = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= 2) return false;
  const status = (error as AxiosError | undefined)?.response?.status;
  return status === undefined || status >= 500;
};
const clockRetryDelay = (attempt: number): number =>
  Math.min(1000 * 2 ** attempt, 4000);

export type Shift = Database["public"]["Tables"]["shifts"]["Row"] & {
  // US-103: definition link (database.ts not yet regenerated for the column).
  shift_definition_id?: string | null;
  facilities?: { id: string; name: string; timezone?: string | null };
  employees?: {
    id: string;
    first_name: string;
    last_name: string;
    position: string;
    phone?: string;
    email?: string;
  };
  has_conflict?: boolean;
  is_overtime?: boolean;
  overtime_hours?: number;
};

export type ShiftInsert = Database["public"]["Tables"]["shifts"]["Insert"];
export type ShiftUpdate = Database["public"]["Tables"]["shifts"]["Update"];

/**
 * Fetch shifts with optional filters
 *
 * **Facility Context Integration:**
 * - If `filters.facility_id` is provided, uses that value
 * - Otherwise, falls back to current facility from context
 * - This ensures shift-based staff see their facility's shifts automatically
 */
export function useShifts(filters?: {
  facility_id?: string;
  employee_id?: string;
  start_date?: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
  status?: string;
}) {
  const { currentFacilityId } = useFacilityContext();

  // Use provided facility_id or fallback to context
  const effectiveFacilityId = filters?.facility_id ?? currentFacilityId;

  // Rebuild filters with effective facility_id
  const effectiveFilters = {
    ...filters,
    facility_id: effectiveFacilityId ?? undefined,
  };

  return useQuery({
    queryKey: ["shifts", effectiveFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (effectiveFilters.facility_id)
        params.append("facility_id", effectiveFilters.facility_id);
      if (effectiveFilters.employee_id)
        params.append("employee_id", effectiveFilters.employee_id);
      if (effectiveFilters.start_date)
        params.append("start_date", effectiveFilters.start_date);
      if (effectiveFilters.end_date)
        params.append("end_date", effectiveFilters.end_date);
      if (effectiveFilters.status)
        params.append("status", effectiveFilters.status);

      const { data } = await api.get(`/shifts?${params.toString()}`);
      // overtime_hours is a Prisma Decimal — it arrives as a STRING in JSON
      // despite the `number` type, so `.toFixed()` / `sum + x` break on the
      // Schedule page (Sentry: /schedule "reduce(...).toFixed is not a
      // function"). Coerce at the boundary so every consumer gets a real number.
      return (data.data as Shift[]).map((s) => {
        const ot = s.overtime_hours as unknown;
        return { ...s, overtime_hours: ot == null ? undefined : Number(ot) };
      }) as Shift[];
    },
  });
}

/**
 * Fetch a single shift by ID
 */
export function useShift(shiftId: string) {
  return useQuery({
    queryKey: ["shifts", shiftId],
    queryFn: async () => {
      const { data } = await api.get(`/shifts/${shiftId}`);
      return data.data as Shift;
    },
    enabled: !!shiftId,
  });
}

/**
 * Create a new shift
 */
export function useCreateShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shift: ShiftInsert) => {
      const { data } = await api.post("/shifts", shift);
      return data.data as Shift;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      notifications.show({
        title: "Success",
        message: "Shift created successfully",
        color: "green",
      });
    },
    // US-081: ShiftForm owns error UX via handleFormErrors (inline 400→field +
    // single toast); suppress the global handler so it isn't double-signalled.
    meta: FORM_MUTATION_META,
  });
}

/**
 * Update an existing shift
 */
export function useUpdateShift(shiftId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shift: ShiftUpdate) => {
      const { data } = await api.put(`/shifts/${shiftId}`, shift);
      return data.data as Shift;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      queryClient.invalidateQueries({ queryKey: ["shifts", shiftId] });
      notifications.show({
        title: "Success",
        message: "Shift updated successfully",
        color: "green",
      });
    },
    // US-081: ShiftForm owns error UX via handleFormErrors (single signal).
    meta: FORM_MUTATION_META,
  });
}

/**
 * Delete a shift
 */
export function useDeleteShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shiftId: string) => {
      const { data } = await api.delete(`/shifts/${shiftId}`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      notifications.show({
        title: "Success",
        message: "Shift deleted successfully",
        color: "green",
      });
    },
    // US-081: global MutationCache handler fires this fallback; no per-hook
    // onError needed.
    meta: { errorFallback: "Failed to delete shift" },
  });
}

/**
 * Clock out of a shift
 */
export function useClockOut() {
  const queryClient = useQueryClient();

  return useMutation({
    retry: retryClockAction,
    retryDelay: clockRetryDelay,
    mutationFn: async (shiftId: string) => {
      const { data } = await api.post(`/shifts/${shiftId}/clock`, {
        action: "clock_out",
      });
      return data.data as Shift;
    },
    onSuccess: async (data, shiftId) => {
      // CRITICAL FIX: Update cache with the returned shift data instead of invalidating
      // This avoids race conditions with eventual consistency in Supabase replicas
      if (data) {
        // Update ALL shifts queries (they have different filter query keys)
        queryClient.setQueriesData(
          { queryKey: ["shifts"] },
          (oldData: Shift[] | undefined) => {
            if (!oldData) return oldData;
            return oldData.map((shift) =>
              shift.id === shiftId ? { ...shift, ...data } : shift,
            );
          },
        );

        // Update the single shift query cache
        queryClient.setQueryData(["shifts", shiftId], data);
      }

      // CRITICAL FIX: Force immediate refetch of auth context instead of just invalidating
      // This ensures protected routes see updated isOnShift status immediately
      await queryClient.refetchQueries({ queryKey: ["auth-context"] });

      // SCRUM-162: confirm the captured check-out time + duration on duty.
      const message = (() => {
        const out = data?.clock_out_time;
        if (!out) return "You are now off duty";
        const timeStr = new Date(out).toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
        });
        const inAt = data?.clock_in_time;
        if (!inAt) return `Checked out at ${timeStr}`;
        const mins = Math.max(
          0,
          Math.floor(
            (new Date(out).getTime() - new Date(inAt).getTime()) / 60000,
          ),
        );
        const dur =
          mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
        return `Checked out at ${timeStr} · ${dur} on duty`;
      })();
      notifications.show({
        title: "Checked out",
        message,
        color: "green",
      });
    },
    // US-081: global MutationCache handler fires this fallback; no per-hook
    // onError needed.
    meta: { errorFallback: "Failed to check out" },
  });
}

/**
 * US-176 — re-register this device against the NEW check-in, silently.
 *
 * A device credential is bound to the check-in it was minted in
 * (employee_reauth_credentials.shift_id), and the Cognito verify Lambda only
 * accepts one whose own check-in is still open and under 12h. So the moment
 * someone clocks out and back in, PIN sign-in starts failing — and Cognito
 * can only report that as a generic failure, which the lock screen showed as
 * "Incorrect PIN" (live, iPad, 2026-09-20).
 *
 * The PIN itself is NOT re-set: it lives in employee_pins, one row per
 * employee, and survives check-ins. This only re-mints the device key, so the
 * same PIN keeps working.
 *
 * Best-effort and silent — a failure here just means the next PIN sign-in
 * falls back to the password flow, exactly as before. Skipped when:
 *   - the shell can't hold a credential (plain web),
 *   - the build has no PIN client,
 *   - this device never had fast sign-in set up (nothing to re-register),
 *   - the session is itself a PIN session: the backend refuses enrolment from
 *     one by design, so a stolen PIN session can never re-enrol itself
 *     (requireMfaProvenance). Those users re-register at their next full
 *     sign-in.
 */
async function reRegisterDeviceForNewCheckIn(
  context: unknown,
): Promise<void> {
  const ctx = context as
    | { email?: string; authFlow?: "full" | "pin" }
    | undefined;
  if (!ctx?.email || ctx.authFlow !== "full") return;
  if (!reauthCredential.available || !isReauthClientConfigured()) return;
  if (!listReauthProfiles().some((p) => p.email === ctx.email)) return;
  await mintReauthCredentialIfPossible(ctx.email);
}

/**
 * Check in to duty — creates a presence shift already clocked-in.
 * POST /api/shifts with { facility_id, shift_type? }
 */
export function useCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      facility_id: string;
      shift_type?: "day" | "evening" | "night" | "on_call";
    }) => {
      const { data } = await api.post("/shifts", payload);
      return data.data as Shift;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["auth-context"] });
      void reRegisterDeviceForNewCheckIn(
        queryClient.getQueryData(["auth-context"]),
      ).catch(() => {
        // Best-effort: the next PIN sign-in just falls back to the password
        // flow. Never surface as an unhandled rejection off a check-in.
      });
      notifications.show({
        title: "Checked In",
        message: "You are now on duty",
        color: "green",
      });
    },
    meta: { errorFallback: "Failed to check in" },
  });
}

/**
 * SCRUM-162 — an employee's presence (check-in) history across ALL their
 * facilities. Unlike {@link useShifts}, this does NOT scope to the current
 * facility (presence isn't facility-bound from the viewer's perspective) and
 * returns only rows that actually have a check-in, newest first. Backs the
 * presence-log table on the Profile + Employee detail pages.
 */
export function usePresenceHistory(employeeId: string | null | undefined) {
  return useQuery({
    queryKey: ["presence-history", employeeId],
    queryFn: async () => {
      const params = new URLSearchParams({
        employee_id: employeeId as string,
      });
      const { data } = await api.get(`/shifts?${params.toString()}`);
      return (data.data as Shift[])
        .filter((s) => s.clock_in_time)
        .sort(
          (a, b) =>
            new Date(b.clock_in_time as string).getTime() -
            new Date(a.clock_in_time as string).getTime(),
        );
    },
    enabled: !!employeeId,
  });
}
